import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiTrendingUp, FiTrendingDown, FiCheck, FiX, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { formatNumber, formatDate } from '../utils/numberFormatter';
import { CustomerService } from '../services/CustomerService';
import { colors, typography, spacing, borderRadius, shadows, animations } from '../styles/designTokens';
import Button from './ui/Button';
import Input from './ui/Input';

// 모바일 포인트 내역 컨테이너
const MobilePointContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
`;

// 모바일 포인트 내역 헤더
const MobilePointHeader = styled.div`
  background: ${colors.semantic.background.secondary};
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 포인트 내역 제목
const MobilePointTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0;
  line-height: 1.2;
`;



// 모바일 인라인 포인트 관리 폼 스타일 - 개선된 레이아웃
const MobileInlinePointForm = styled.div`
  background: ${colors.semantic.background.secondary};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[4]};
  margin-bottom: ${spacing[4]};
  box-shadow: ${shadows.sm};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  width: 100%;
  
  &:hover {
    box-shadow: ${shadows.base};
  }
  
  @media (min-width: 480px) {
    padding: ${spacing[3]};
    margin-bottom: ${spacing[3]};
  }
`;

// 모바일 폼 컨테이너 스타일 - 개선된 레이아웃
const MobileFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  width: 100%;
`;

// 모바일 폼 행 스타일 - 개선된 레이아웃
const MobileFormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
  
  @media (min-width: 480px) {
    flex-direction: row;
    align-items: center;
  }
`;

// 모바일 포인트 타입 선택 스타일 - 개선된 레이아웃
const MobilePointTypeSelect = styled.select`
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.lg};
  background: ${colors.semantic.background.primary};
  color: ${colors.semantic.text.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.sans};
  cursor: pointer;
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  height: 44px;
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${colors.primary[100]};
  }
  
  &:hover {
    border-color: ${colors.semantic.border.secondary};
  }
  
  @media (min-width: 480px) {
    flex: 1;
    height: 40px;
    min-height: 40px;
  }
`;

// 모바일 포인트 금액 입력 래퍼 스타일 - 개선된 레이아웃
const MobilePointAmountWrapper = styled.div`
  position: relative;
  width: 100%;
  
  .point-addon {
    position: absolute;
    top: 50%;
    right: ${spacing[3]};
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.primary[600]};
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.bold};
    background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[100]} 100%);
    border: 1px solid ${colors.primary[200]};
    border-radius: ${borderRadius.md};
    padding: ${spacing[1]} ${spacing[2]};
    z-index: 1;
    box-shadow: 0 1px 2px rgba(59, 130, 246, 0.1);
    transition: all ${animations.duration.normal} ${animations.easing.smooth};
    min-width: 32px;
    height: 28px;
    
    &:hover {
      background: linear-gradient(135deg, ${colors.primary[100]} 0%, ${colors.primary[200]} 100%);
      border-color: ${colors.primary[300]};
      box-shadow: 0 2px 4px rgba(59, 130, 246, 0.15);
    }
  }
  
  @media (min-width: 480px) {
    flex: 1;
    
    .point-addon {
      right: ${spacing[2]};
      font-size: ${typography.fontSize.xs};
      padding: 2px ${spacing[1]};
      min-width: 28px;
      height: 24px;
    }
  }
`;

// 모바일 폼 액션 버튼 컨테이너 스타일 - 개선된 레이아웃
const MobileFormActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
  width: 100%;
  
  @media (min-width: 480px) {
    margin-top: ${spacing[2]};
  }
`;

// 모바일 메시지 스타일
const MobileMessageContainer = styled.div<{ type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  margin-top: ${spacing[2]};
  animation: ${animations.keyframes.slideUp} ${animations.duration.normal} ${animations.easing.smooth};
  
  ${props => props.type === 'success' ? `
    background: ${colors.success[50]};
    color: ${colors.success[700]};
    border: 1px solid ${colors.success[200]};
  ` : `
    background: ${colors.error[50]};
    color: ${colors.error[700]};
    border: 1px solid ${colors.error[200]};
  `}
`;

// 모바일 포인트 미리보기 스타일 - 개선된 레이아웃
const MobilePointPreview = styled.div`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]} ${spacing[4]};
  margin-top: ${spacing[3]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.primary};
  font-weight: ${typography.fontWeight.medium};
  min-height: 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  width: 100%;
  
  .preview-label {
    color: ${colors.semantic.text.tertiary};
    font-size: ${typography.fontSize.xs};
    margin-bottom: ${spacing[1]};
    font-weight: ${typography.fontWeight.medium};
  }
  
  .preview-content {
    color: ${colors.semantic.text.primary};
    font-weight: ${typography.fontWeight.semibold};
    word-break: break-word;
    line-height: 1.4;
  }
  
  @media (min-width: 480px) {
    min-height: 40px;
    padding: ${spacing[2]} ${spacing[3]};
    font-size: ${typography.fontSize.xs};
    
    .preview-content {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

// 모바일 포인트 카드 - 컴팩트 디자인
const MobilePointCard = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${colors.secondary[100]};
  
  &:last-child {
    border-bottom: none;
  }
`;

// 모바일 포인트 카드 헤더 - 컴팩트
const MobilePointCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;

// 모바일 포인트 설명 - 컴팩트
const MobilePointDescription = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0;
  flex: 1;
  line-height: 1.2;
`;

// 모바일 포인트 금액 - 컴팩트
const MobilePointAmount = styled.div<{ isPositive: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 13px;
  font-weight: 700;
  color: ${props =>
    props.isPositive ? colors.success[600] : colors.error[600]};
  text-align: right;
  margin-left: 6px;
`;

// 모바일 포인트 정보 그리드 - 컴팩트
const MobilePointInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  font-size: 11px;
  margin-top: 2px;
`;

// 모바일 포인트 정보 아이템 - 컴팩트
const MobilePointInfoItem = styled.div`
  color: ${colors.semantic.text.tertiary};

  .label {
    font-weight: 500;
    margin-bottom: 1px;
    font-size: 10px;
  }

  .value {
    color: ${colors.semantic.text.secondary};
    font-size: 11px;
    line-height: 1.2;
  }
`;

// 모바일 포인트 잔액 - 컴팩트
const MobilePointBalance = styled.div`
  background: ${colors.semantic.background.secondary};
  padding: 4px 8px;
  border-radius: ${borderRadius.base};
  margin-top: 4px;
  font-size: 10px;
  color: ${colors.semantic.text.tertiary};

  .balance-label {
    font-weight: 500;
    margin-bottom: 1px;
  }

  .balance-value {
    font-weight: 600;
    color: ${colors.secondary[800]};
  }
`;

// 모바일 로딩 스피너
const MobileLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  
  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid ${colors.semantic.border.primary};
    border-top: 2px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 모바일 에러 메시지
const MobileErrorMessage = styled.div`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  color: ${colors.error[600]};
  padding: 12px 16px;
  border-radius: ${borderRadius.md};
  text-align: center;
  font-size: 12px;
  margin: 16px;
  line-height: 1.3;
`;

// 모바일 빈 상태 메시지
const MobileEmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: ${colors.semantic.text.tertiary};

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: ${colors.semantic.text.secondary};
    margin: 0 0 6px 0;
  }
  
  p {
    font-size: 12px;
    margin: 0;
    line-height: 1.3;
  }
`;

// 포인트 내역 데이터 타입 (API 응답 기반)
interface PointHistoryData {
  생성일자: string;
  설명: string;
  유형: string;
  포인트: number;
  잔여포인트: number;
}

// 모바일 포인트 내역 테이블 컴포넌트
interface PointHistoryTableMobileProps {
  userIdx: number;
  onPointUpdate?: () => void; // 포인트 업데이트 콜백
}

const PointHistoryTableMobile: React.FC<PointHistoryTableMobileProps> = ({ userIdx, onPointUpdate }) => {
  const [pointHistory, setPointHistory] = useState<PointHistoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 인라인 포인트 관리 폼 상태
  const [pointType, setPointType] = useState<boolean>(true); // true: 충전, false: 차감
  const [pointAmount, setPointAmount] = useState<string>('');
  const [pointDesc, setPointDesc] = useState<string>('');
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [formMessage, setFormMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const fetchPointHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('📱 모바일 포인트 내역 API 호출 시작:', userIdx);
        const response = await CustomerService.getPointHistory({
          userIdx,
          page: 1,
          limit: 15
        });

        console.log('📱 모바일 포인트 내역 API 응답 성공:', response);
        setPointHistory(response.items || []);
      } catch (err: any) {
        console.error('📱 모바일 포인트 내역 조회 실패:', err);
        setError(err.message || '포인트 내역을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    if (userIdx) {
      fetchPointHistory();
    }
  }, [userIdx]);

  const getPointIcon = (type: string) => {
    switch (type) {
      case '적립':
        return <FiTrendingUp size={12} />;
      case '차감':
        return <FiTrendingDown size={12} />;
      default:
        return <FiTrendingUp size={12} />;
    }
  };

  const getPointTypeLabel = (type: string) => {
    return type; // API에서 이미 한국어로 제공
  };

  const isPositive = (type: string) => type === '적립';

  // 포인트 내역 새로고침 함수
  const refreshPointHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📱 모바일 포인트 내역 새로고침 시작:', userIdx);
      const response = await CustomerService.getPointHistory({
        userIdx,
        page: 1,
        limit: 15
      });

      console.log('📱 모바일 포인트 내역 새로고침 성공:', response);
      setPointHistory(response.items || []);
    } catch (err: any) {
      console.error('📱 모바일 포인트 내역 새로고침 실패:', err);
      setError(err.message || '포인트 내역을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 포인트 타입 변경 핸들러
  const handlePointTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPointType(e.target.value === 'true');
    setFormMessage(null);
  };

  // 포인트 금액 변경 핸들러 - 회계처리 반영
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // 빈 값이거나 숫자만 입력 허용 (콤마 제거 후 검증)
    const numericValue = value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(numericValue)) {
      // 숫자에 천 단위 콤마 추가
      if (numericValue === '') {
        setPointAmount('');
      } else {
        const formattedValue = parseInt(numericValue).toLocaleString();
        setPointAmount(formattedValue);
      }
      setFormMessage(null);
    }
  };

  // 포인트 설명 변경 핸들러
  const handleDescChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPointDesc(e.target.value);
    setFormMessage(null);
  };

  // 폼 초기화
  const resetForm = () => {
    setPointAmount('');
    setPointDesc('');
    setFormMessage(null);
  };

  // 유효성 검사
  const validateForm = () => {
    if (!pointAmount || pointAmount === '0') {
      setFormMessage({ type: 'error', text: '포인트 금액을 입력해주세요.' });
      return false;
    }

    const amount = parseInt(pointAmount);
    if (amount <= 0) {
      setFormMessage({ type: 'error', text: '포인트 금액은 0보다 커야 합니다.' });
      return false;
    }

    if (amount > 1000000) {
      setFormMessage({ type: 'error', text: '포인트 금액은 1,000,000을 초과할 수 없습니다.' });
      return false;
    }

    if (!pointDesc.trim()) {
      setFormMessage({ type: 'error', text: '포인트 설명을 입력해주세요.' });
      return false;
    }

    if (pointDesc.trim().length > 100) {
      setFormMessage({ type: 'error', text: '포인트 설명은 100자를 초과할 수 없습니다.' });
      return false;
    }

    return true;
  };

  // 포인트 처리 핸들러
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setFormLoading(true);
    setFormMessage(null);

    try {
      const result = await CustomerService.updatePoints({
        userIdx,
        pointType,
        pointAmount: parseInt(pointAmount.replace(/,/g, '')),
        pointDesc: pointDesc.trim()
      });

      if (result.success) {
        setFormMessage({
          type: 'success',
          text: result.message
        });
        resetForm();
        // 포인트 내역 새로고침
        refreshPointHistory();
        // 상위 컴포넌트에 포인트 업데이트 알림
        if (onPointUpdate) {
          onPointUpdate();
        }
        // 3초 후 성공 메시지 자동 제거
        setTimeout(() => {
          setFormMessage(null);
        }, 3000);
      } else {
        setFormMessage({
          type: 'error',
          text: result.message
        });
      }
    } catch (err) {
      console.error('포인트 처리 실패:', err);
      setFormMessage({
        type: 'error',
        text: '포인트 처리 중 오류가 발생했습니다.'
      });
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <MobilePointContainer>
        <MobilePointHeader>
          <MobilePointTitle>포인트 내역</MobilePointTitle>
        </MobilePointHeader>
        <MobileLoadingContainer>
          <div className="spinner" />
        </MobileLoadingContainer>
      </MobilePointContainer>
    );
  }

  if (error) {
    return (
      <MobilePointContainer>
        <MobilePointHeader>
          <MobilePointTitle>포인트 내역</MobilePointTitle>
        </MobilePointHeader>
        <MobileErrorMessage>{error}</MobileErrorMessage>
      </MobilePointContainer>
    );
  }

  return (
    <MobilePointContainer>
      {/* 인라인 포인트 관리 폼 - 컴팩트 */}
      <MobileInlinePointForm>
        <MobileFormContainer>
          <MobileFormRow>
            <MobilePointTypeSelect
              value={pointType.toString()}
              onChange={handlePointTypeChange}
              disabled={formLoading}
            >
              <option value="true">💎 충전</option>
              <option value="false">📉 차감</option>
            </MobilePointTypeSelect>

            <Input
              type="text"
              value={pointDesc}
              onChange={handleDescChange}
              placeholder="포인트 설명"
              disabled={formLoading}
              size="sm"
              variant="default"
            />
          </MobileFormRow>

          <MobileFormRow>
            <MobilePointAmountWrapper>
              <Input
                type="text"
                value={pointAmount}
                onChange={handleAmountChange}
                placeholder="금액"
                disabled={formLoading}
                size="sm"
                variant="default"
              />
              <div className="point-addon">P</div>
            </MobilePointAmountWrapper>
          </MobileFormRow>

          <MobileFormActions>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={formLoading || !pointAmount || !pointDesc.trim()}
              state={formLoading ? 'loading' : 'default'}
              leftIcon={<FiCheck size={12} />}
            >
              {formLoading ? '처리 중...' : '처리'}
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={resetForm}
              disabled={formLoading}
              leftIcon={<FiX size={12} />}
            >
              초기화
            </Button>
          </MobileFormActions>
        </MobileFormContainer>

        {/* 포인트 미리보기 - 항상 표시 */}
        <MobilePointPreview>
          <div className="preview-label">미리보기</div>
          <div className="preview-content">
            {pointAmount && pointDesc ?
              `[${pointDesc}] ${pointAmount} 포인트 ${pointType ? '충전' : '차감'}` :
              '입력하세요'
            }
          </div>
        </MobilePointPreview>

        {/* 메시지 */}
        {formMessage && (
          <MobileMessageContainer type={formMessage.type}>
            {formMessage.type === 'success' ? (
              <FiCheckCircle size={12} />
            ) : (
              <FiAlertCircle size={12} />
            )}
            {formMessage.text}
          </MobileMessageContainer>
        )}
      </MobileInlinePointForm>

      <MobilePointHeader>
        <MobilePointTitle>
          포인트 내역 {pointHistory && pointHistory.length > 0 && `(${pointHistory.length}건)`}
        </MobilePointTitle>
      </MobilePointHeader>

      {!pointHistory || pointHistory.length === 0 ? (
        <MobileEmptyState>
          <h3>포인트 내역이 없습니다</h3>
          <p>아직 포인트 사용 내역이 없습니다.</p>
        </MobileEmptyState>
      ) : (
        pointHistory.map((point, index) => (
          <MobilePointCard key={`${point.생성일자}-${index}`}>
            <MobilePointCardHeader>
              <MobilePointDescription>{point.설명}</MobilePointDescription>
              <MobilePointAmount isPositive={isPositive(point.유형)}>
                {getPointIcon(point.유형)}
                {isPositive(point.유형) ? '+' : ''}{formatNumber(point.포인트)}P
              </MobilePointAmount>
            </MobilePointCardHeader>

            <MobilePointInfoGrid>
              <MobilePointInfoItem>
                <div className="label">유형</div>
                <div className="value">{getPointTypeLabel(point.유형)}</div>
              </MobilePointInfoItem>
              <MobilePointInfoItem>
                <div className="label">일시</div>
                <div className="value">{formatDate(point.생성일자)}</div>
              </MobilePointInfoItem>
              <MobilePointInfoItem>
                <div className="label">포인트</div>
                <div className="value">{formatNumber(point.포인트)}P</div>
              </MobilePointInfoItem>
              <MobilePointInfoItem>
                <div className="label">잔액</div>
                <div className="value">{formatNumber(point.잔여포인트)}P</div>
              </MobilePointInfoItem>
            </MobilePointInfoGrid>

            <MobilePointBalance>
              <div className="balance-label">거래 후 잔액</div>
              <div className="balance-value">{formatNumber(point.잔여포인트)}P</div>
            </MobilePointBalance>
          </MobilePointCard>
        ))
      )}

    </MobilePointContainer>
  );
};

export default PointHistoryTableMobile;
